import { useEffect, useState } from "react";
import { Button, Card, Cluster, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface Finding {
  ecosystem: "python" | "npm";
  package_name: string;
  installed_version: string;
  vulnerability_id: string;
  severity: string | null;
  fix_versions: string[];
  description: string | null;
}

interface ScanRun {
  id: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  python_findings: number;
  npm_findings: number;
  new_findings: number;
  email_sent_at: string | null;
  error_message: string | null;
}

interface DependencyScanDashboard {
  runs: ScanRun[];
  latest_findings: Finding[];
  recipients: string[];
  schedule: string;
}

function severityLabel(severity: string | null): string {
  return severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : "Unrated";
}

export function DependencySecurityPage() {
  const [data, setData] = useState<DependencyScanDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setError(null);
    try {
      const payload = await api.get<DependencyScanDashboard>("/operator/dependency-scans");
      setData(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to load dependency scans");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function runNow() {
    setBusy(true);
    setError(null);
    try {
      await api.post("/operator/dependency-scans/run", {});
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Unable to run scan");
    } finally {
      setBusy(false);
    }
  }

  const latest = data?.runs?.[0] ?? null;
  const findings = data?.latest_findings ?? [];

  if (!data && !error) {
    return <p>Loading…</p>;
  }

  return (
    <Stack gap={5}>
      <Cluster justify="between" align="center">
        <div>
          <h1>Dependency security</h1>
          <p className="admin-muted">
            Monthly pip-audit + npm audit scan of what is actually deployed ({data?.schedule ?? "1st of every month"}).
            This catches a CVE published after the last deploy, when no CI/CD run would otherwise notice it.
            Worker emails {data?.recipients?.join(", ") || "support@intellilox.com"} only when a new finding appears
            since the previous scan. Remediation is manual: investigate in dev, then trigger a normal deploy.
          </p>
        </div>
        <Button disabled={busy} onClick={() => runNow()}>
          {busy ? "Running…" : "Run scan now"}
        </Button>
      </Cluster>

      {error ? <Flash tone="error">{error}</Flash> : null}

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Latest scan"
            tip="Most recent completed or failed run. New findings are new since the previous completed scan, not new since the package was first installed."
            as="h2"
          />
          {!latest ? (
            <p className="admin-muted">No scan has run yet. Use Run scan now, or wait for the 1st of the month.</p>
          ) : (
            <>
              <p>
                {formatUtc(latest.started_at)} · {latest.status}
                {latest.email_sent_at ? ` · alert emailed ${formatUtc(latest.email_sent_at)}` : ""}
              </p>
              <p className="admin-muted">
                Python findings {latest.python_findings} · npm findings {latest.npm_findings} · new this run{" "}
                {latest.new_findings}
              </p>
              {latest.error_message ? <Flash tone="error">{latest.error_message}</Flash> : null}
            </>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Current findings"
            tip="Every open finding from the latest completed scan, not just what's new. Fix column shows the earliest version that resolves it, when published."
            as="h2"
          />
          {findings.length === 0 ? (
            <p className="admin-muted">No open findings in the latest completed scan.</p>
          ) : (
            <div className="admin-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Ecosystem</th>
                    <th>Package</th>
                    <th>Installed</th>
                    <th>Advisory</th>
                    <th>Severity</th>
                    <th>Fix</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((row) => (
                    <tr key={`${row.ecosystem}-${row.package_name}-${row.vulnerability_id}`}>
                      <td>{row.ecosystem}</td>
                      <td>{row.package_name}</td>
                      <td><code>{row.installed_version}</code></td>
                      <td><code>{row.vulnerability_id}</code></td>
                      <td>{severityLabel(row.severity)}</td>
                      <td>{row.fix_versions.length ? row.fix_versions.join(", ") : "not published yet"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Scan history"
            tip="Past runs, oldest at the bottom. A failed run means pip-audit or npm audit itself errored, not that packages were found vulnerable."
            as="h2"
          />
          {!data?.runs?.length ? (
            <p className="admin-muted">No prior runs.</p>
          ) : (
            <div className="admin-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Started (UTC)</th>
                    <th>Status</th>
                    <th>Python</th>
                    <th>npm</th>
                    <th>New</th>
                    <th>Emailed</th>
                  </tr>
                </thead>
                <tbody>
                  {data.runs.map((run) => (
                    <tr key={run.id}>
                      <td className="admin-readonly">{formatUtc(run.started_at)}</td>
                      <td>{run.status}</td>
                      <td>{run.python_findings}</td>
                      <td>{run.npm_findings}</td>
                      <td>{run.new_findings}</td>
                      <td>{formatUtc(run.email_sent_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
